import { useState, useRef, useEffect } from 'react';
import { AuthLayout } from '../layout/AuthLayout.jsx';
import { FormField } from '../components/FormField.jsx';
import { TextInput } from '../components/TextInput.jsx';
import { PasswordInput } from '../components/PasswordInput.jsx';
import { SubmitButton } from '../components/SubmitButton.jsx';
import { FormError } from '../components/FormError.jsx';
import { api, ApiError } from '../api/client.js';

export function SignupPage({ navigate }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const firstRef = useRef(null);

  useEffect(() => { firstRef.current?.focus(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !accepted) return;
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      await api.signup({
        full_name: fullName.trim(),
        email: email.trim(),
        company_name: companyName.trim() || undefined,
        password,
      });
      navigate('/verify-email', `email=${encodeURIComponent(email.trim())}`);
    } catch (e2) {
      if (e2 instanceof ApiError && e2.fieldErrors) {
        setFieldErrors(e2.fieldErrors);
      }
      setError(e2);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Create your account" subtitle="Start analysing schedules in minutes.">
      <form onSubmit={handleSubmit} noValidate>
        <FormError error={error} />
        <FormField label="Full name" htmlFor="signup-name" error={fieldErrors.full_name}>
          <TextInput
            id="signup-name"
            name="full_name"
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            ref={firstRef}
            error={!!fieldErrors.full_name}
            required
          />
        </FormField>
        <FormField label="Email" htmlFor="signup-email" error={fieldErrors.email}>
          <TextInput
            id="signup-email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!fieldErrors.email}
            required
          />
        </FormField>
        <FormField
          label="Company (optional)"
          htmlFor="signup-company"
          error={fieldErrors.company_name}
        >
          <TextInput
            id="signup-company"
            name="company_name"
            autoComplete="organization"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            error={!!fieldErrors.company_name}
          />
        </FormField>
        <FormField
          label="Password"
          htmlFor="signup-password"
          hint="At least 10 characters, with a letter and a digit."
          error={fieldErrors.password}
        >
          <PasswordInput
            id="signup-password"
            name="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!fieldErrors.password}
            required
          />
        </FormField>
        <div className="signup-accept">
          <label className="signup-accept-label">
            <input
              type="checkbox"
              name="accept_terms"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              required
            />
            <span>
              I have read and agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </span>
          </label>
        </div>
        <SubmitButton loading={submitting} disabled={!accepted}>Create account</SubmitButton>
      </form>
      <div className="auth-links">
        <span>Already have an account?</span>{' '}
        <a
          href="/login"
          onClick={(ev) => { ev.preventDefault(); navigate('/login'); }}
        >Log in</a>
      </div>
    </AuthLayout>
  );
}
